import React, { useEffect, useState, useRef, lazy } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { CTAButton } from '../ui/CTAButton';
import { ShieldCheckIcon, BrainIcon, ZapIcon, TargetIcon, PlayIcon } from 'lucide-react';
export function HeroSection() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.05
  });
  return <section ref={ref} className="relative w-full min-h-screen flex items-center justify-center overflow-hidden" style={{
    background: 'linear-gradient(180deg, #0f0f1e 0%, #1a1a2e 40%, #16213e 100%)'
  }} data-id="element-140">
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, rgba(201,168,76,0.07) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-141" />

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: `linear-gradient(rgba(201,168,76,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.3) 1px, transparent 1px)`,
      backgroundSize: '60px 60px'
    }} aria-hidden="true" data-id="element-142" />

      <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 py-20 md:py-28 text-center" data-id="element-143">
        {/* Main Headline - MASSIVE CURIOSITY */}
        <h1 className={`
            font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl
            font-bold leading-[1.1] mb-10
            text-[#e8e6e1]
            transition-all duration-1000 delay-200
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
          `} data-id="element-144">
          ¿Y si el problema{' '}
          <span className="text-gold-gradient" data-id="element-145">no es lo que crees?</span>
        </h1>

        {/* Subheadline */}
        <p className={`
            text-xl md:text-2xl lg:text-3xl text-[#9a9aab] max-w-3xl mx-auto mb-16
            leading-relaxed
            transition-all duration-1000 delay-300
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
          `} data-id="element-146">
          Miles de personas están{' '}
          <span className="text-[#e8e6e1] font-semibold" data-id="element-147">
            rompiendo el ciclo
          </span>{' '}
          de procrastinación y autosabotaje.
          <br data-id="element-148" />
          <span className="text-[#c9a84c]" data-id="element-149">No con más motivación.</span>
        </p>

        {/* CTA */}
        <div className={`
            flex flex-col items-center gap-6
            transition-all duration-1000 delay-400
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
          `} data-id="element-150">
          <a href="#dolor" className="inline-flex items-center justify-center gap-3 px-12 py-6 text-xl md:text-2xl
              font-semibold tracking-wide text-[#1a1a2e]
              bg-gradient-to-r from-[#c9a84c] via-[#f0d060] to-[#c9a84c] bg-[length:200%_auto]
              rounded-lg cta-pulse
              transition-all duration-500 ease-out
              hover:bg-[position:right_center] hover:scale-[1.03] hover:shadow-[0_0_50px_rgba(201,168,76,0.5)]
              active:scale-[0.98]
              focus:outline-none focus:ring-2 focus:ring-[#f0d060] focus:ring-offset-2 focus:ring-offset-[#1a1a2e]
              cursor-pointer" style={{
          textDecoration: 'none'
        }} data-id="element-151">
            Descubre qué es →
          </a>
          <p className="text-[#6b6b7b] text-sm tracking-wide" data-id="element-152">
            2 minutos que pueden cambiar todo
          </p>
        </div>

        {/* Social proof micro */}
        <div className={`
            mt-20 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10
            transition-all duration-1000 delay-500
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
          `} data-id="element-153">
          <div className="flex items-center gap-2" data-id="element-154">
            <div className="flex -space-x-2" data-id="element-155">
              {['https://randomuser.me/api/portraits/men/32.jpg', 'https://randomuser.me/api/portraits/women/44.jpg', 'https://randomuser.me/api/portraits/men/67.jpg', 'https://randomuser.me/api/portraits/women/21.jpg', 'https://randomuser.me/api/portraits/men/85.jpg'].map((src, i) => <img key={i} src={src} alt="Usuario transformado" className="w-8 h-8 rounded-full border-2 border-[#1a1a2e] object-cover" data-id="element-156" />)}
            </div>
            <span className="text-[#9a9aab] text-sm" data-id="element-157">
              +2,400 personas transformadas
            </span>
          </div>
          <div className="flex items-center gap-1" data-id="element-158">
            {[1, 2, 3, 4, 5].map(i => <svg key={i} className="w-4 h-4 text-[#c9a84c]" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" data-id="element-159">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-160" />
              </svg>)}
            <span className="text-[#9a9aab] text-sm ml-1" data-id="element-161">
              4.9/5 satisfacción
            </span>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{
      background: 'linear-gradient(to top, #1a1a2e, transparent)'
    }} aria-hidden="true" data-id="element-162" />
    </section>;
}